import { useEffect, useState, useRef } from "react";
import api, { apiErr } from "@/lib/api";
import { useBlobUrl } from "@/hooks/useBlobUrl";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Film, UploadCloud, Loader2, Plus, Trash2, Info, Clapperboard, Pencil } from "lucide-react";
import { toast } from "sonner";

function TemplateCard({ t, onDelete, onEdit }) {
  const blobUrl = useBlobUrl(`/templates/${t.id}/preview`, !t.playback_url);
  const previewUrl = t.playback_url || blobUrl;
  return (
    <div data-testid={`template-card-${t.id}`} className="rounded-lg border border-stone-200 bg-white overflow-hidden">
      {previewUrl ? (
        <video src={previewUrl} controls className="w-full aspect-video bg-black" preload="metadata" />
      ) : (
        <div className="w-full aspect-video bg-black flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-stone-500" />
        </div>
      )}
      <div className="p-4 flex items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="font-heading font-semibold text-stone-900 truncate">{t.asana_name}</p>
          <p className="text-xs text-stone-400 truncate">{t.original_name}{t.duration ? ` · ${Math.round(t.duration)}s` : ""}</p>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button onClick={() => onEdit(t)} data-testid={`edit-template-${t.id}`} title="Edit asana name"
            className="p-2 rounded-md text-stone-500 hover:bg-stone-100 hover:text-stone-900">
            <Pencil className="h-4 w-4" />
          </button>
          <button onClick={() => onDelete(t)} data-testid={`delete-template-${t.id}`} title="Delete"
            className="p-2 rounded-md text-stone-500 hover:bg-red-50 hover:text-red-600">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function TemplateManagement() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(null);
  const [editName, setEditName] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  const [asana, setAsana] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const inputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/templates");
      setTemplates(data);
    } catch (e) { apiErr(e); } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const pick = (f) => {
    if (!f) return;
    const ext = f.name.toLowerCase().slice(f.name.lastIndexOf("."));
    if (![".mp4", ".mov"].includes(ext)) { toast.error("Template must be MP4 or MOV"); return; }
    setFile(f);
  };

  const save = async () => {
    if (!asana.trim()) { toast.error("Enter an asana name"); return; }
    if (!file) { toast.error("Select a template video"); return; }
    setUploading(true); setProgress(0);
    const fd = new FormData();
    fd.append("asana_name", asana.trim());
    fd.append("file", file);
    try {
      await api.post("/templates", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => setProgress(Math.round((e.loaded * 100) / (e.total || 1))),
      });
      toast.success("Template added");
      setDialog(false); setAsana(""); setFile(null);
      load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setUploading(false); setProgress(0); }
  };

  const del = async (t) => {
    if (!window.confirm(`Delete template "${t.asana_name}"?`)) return;
    try {
      await api.delete(`/templates/${t.id}`);
      toast.success("Template deleted");
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const openEdit = (t) => {
    setEditDialog(t);
    setEditName(t.asana_name || "");
  };

  const saveEdit = async () => {
    if (!editName.trim()) {
      toast.error("Enter an asana name");
      return;
    }
    setSavingEdit(true);
    try {
      await api.put(`/templates/${editDialog.id}`, { asana_name: editName.trim() });
      toast.success("Asana name updated");
      setEditDialog(null);
      setEditName("");
      load();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSavingEdit(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="template-management">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-700">Branding</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-stone-900 mt-1">Template Videos (Asanas)</h1>
          <p className="text-stone-500 mt-2">Landscape company videos that MRs pick from. Each is merged at the bottom of the greeting.</p>
        </div>
        <button onClick={() => setDialog(true)} data-testid="add-template-button"
          className="inline-flex items-center justify-center gap-2 rounded-md bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700">
          <Plus className="h-4 w-4" /> Add Template
        </button>
      </div>

      <div className="flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800">
        <Info className="h-5 w-5 shrink-0 mt-0.5" />
        <p>Add multiple templates, each with an asana name. MRs select one when creating a doctor's greeting. Landscape MP4/MOV, up to 200MB.</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-emerald-600" /></div>
      ) : templates.length === 0 ? (
        <div className="rounded-lg border border-stone-200 bg-white p-12 text-center">
          <Film className="h-10 w-10 mx-auto text-stone-300" />
          <p className="mt-3 text-stone-600 font-medium">No templates yet</p>
          <p className="text-sm text-stone-400">MRs cannot generate videos until at least one template exists.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {templates.map((t) => (
            <TemplateCard key={t.id} t={t} onDelete={del} onEdit={openEdit} />
          ))}
        </div>
      )}

      <Dialog open={dialog} onOpenChange={(o) => { if (!o && !uploading) { setDialog(false); setAsana(""); setFile(null); } }}>
        <DialogContent className="sm:max-w-md" data-testid="add-template-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Add Template</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.12em] text-stone-500">Asana Name</label>
              <input value={asana} onChange={(e) => setAsana(e.target.value)} data-testid="template-asana-input"
                placeholder="e.g. Surya Namaskar"
                className="mt-1.5 h-11 w-full rounded-md border border-stone-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
            </div>
            <div
              onClick={() => !uploading && inputRef.current?.click()}
              data-testid="template-upload-zone"
              className="flex flex-col items-center justify-center min-h-[160px] rounded-lg border-2 border-dashed border-stone-300 bg-stone-50 p-6 text-center cursor-pointer hover:bg-stone-100 hover:border-emerald-400">
              <input ref={inputRef} type="file" accept=".mp4,.mov,video/mp4,video/quicktime" className="hidden"
                data-testid="template-file-input" onChange={(e) => pick(e.target.files?.[0])} />
              {file ? (
                <div className="flex flex-col items-center gap-1">
                  <Clapperboard className="h-8 w-8 text-emerald-600" />
                  <p className="text-sm font-medium text-stone-800">{file.name}</p>
                  <p className="text-xs text-stone-400">{(file.size / 1024 / 1024).toFixed(1)} MB</p>
                </div>
              ) : (
                <>
                  <UploadCloud className="h-8 w-8 text-emerald-600" />
                  <p className="mt-2 text-sm font-medium text-stone-700">Click to select video</p>
                  <p className="text-xs text-stone-400">MP4 or MOV</p>
                </>
              )}
            </div>
            {uploading && (
              <div>
                <div className="flex justify-between text-xs text-stone-500 mb-1"><span>Uploading…</span><span>{progress}%</span></div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-stone-200">
                  <div className="h-full bg-emerald-500 transition-all" style={{ width: `${progress}%` }} />
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <button onClick={() => { setDialog(false); setAsana(""); setFile(null); }} disabled={uploading} className="rounded-md border border-stone-200 px-4 py-2 text-sm font-medium text-stone-700 hover:bg-stone-50">Cancel</button>
            <button onClick={save} disabled={uploading} data-testid="template-save-button"
              className="inline-flex items-center gap-2 rounded-md bg-emerald-600 px-5 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60">
              {uploading && <Loader2 className="h-4 w-4 animate-spin" />} Add
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editDialog} onOpenChange={(o) => { if (!o && !savingEdit) { setEditDialog(null); setEditName(""); } }}>
        <DialogContent className="sm:max-w-md" data-testid="edit-template-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit Asana Name</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-stone-500">
            Update the label MRs see when selecting this template. Existing generated videos will show the new name too.
          </p>
          <div className="py-2">
            <label className="text-xs font-bold uppercase tracking-[0.12em] text-stone-500">Asana Name</label>
            <input value={editName} onChange={(e) => setEditName(e.target.value)} data-testid="edit-template-asana-input"
              placeholder="e.g. Surya Namaskar"
              className="mt-1.5 h-11 w-full rounded-md border border-stone-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
          </div>
          <DialogFooter>
            <button onClick={() => { setEditDialog(null); setEditName(""); }} disabled={savingEdit}
              className="rounded-md border border-stone-200 px-4 py-2 text-sm font-medium text-stone-700 hover:bg-stone-50">
              Cancel
            </button>
            <button onClick={saveEdit} disabled={savingEdit} data-testid="edit-template-save-button"
              className="inline-flex items-center gap-2 rounded-md bg-emerald-600 px-5 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60">
              {savingEdit && <Loader2 className="h-4 w-4 animate-spin" />} Save
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
